import { AfterViewInit, ChangeDetectorRef, Component, HostListener, inject, DOCUMENT } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  GridColumnV2,
  GridStateChangeV2,
  ReactiveComponent,
  ModalService,
  ToastV2Service,
  BackendResponse,
  ToolingVerificationLevelPipe,
  ControlButton,
  ToolingService,
  GridNoDataConfigV2,
  UserRightsService,
  NotificationsV2Module,
  RadioV2Module,
  GridV2Module,
  IconV2Module,
  AssetsModule,
  ServiceErrorV2Component,
} from '@schenck/rotec-components';
import { switchMap, takeUntil } from 'rxjs/operators';
import { createSchedulingFilter } from 'src/components/task-interval.pipe';
import { addTitleAndWidthToGridColumn, PartialColumn } from 'src/pages/config-utils';
import { DownloadService, ToolingTaskService } from 'src/services';
import { ToolingHistorySearchService } from '../tooling-history-search.service';
import { toolingHistoryColumnsBaseConfig } from '../tooling-history-settings.api';
import { ToolingHistorySettingsService } from '../tooling-history-settings.service';
import { ToolingHistory } from '../tooling-history.model';
import { ToolingHistoryModalComponent } from '../tooling-history-modal/tooling-history-modal.component';
import { openErrorToast } from 'src/utils';
import { AsyncPipe } from '@angular/common';
import { Observable } from 'rxjs';
import { PriorityComponent } from '../../../components/priority/priority.component';
import { DateComponent } from '../../../components/date/date.component';
import { ExecutionDateComponent } from '../../../components/execution-date/execution-date.component';
import { TaskIntervalPipe } from '../../../components/task-interval.pipe';
import { TypeEnumPipe } from '../../../components/type-enum.pipe';

@Component({
  selector: 'ma-tooling-history',
  templateUrl: './tooling-history.component.html',
  styleUrls: ['./tooling-history.component.less'],
  imports: [
    NotificationsV2Module,
    RadioV2Module,
    GridV2Module,
    IconV2Module,
    PriorityComponent,
    DateComponent,
    ExecutionDateComponent,
    AsyncPipe,
    TaskIntervalPipe,
    TypeEnumPipe,
    AssetsModule,
    ServiceErrorV2Component,
  ],
})
export class ToolingHistoryComponent extends ReactiveComponent implements AfterViewInit {
  private readonly document = inject<Document>(DOCUMENT);
  private readonly taskService = inject(ToolingTaskService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly downloadService = inject(DownloadService);
  private readonly translate = inject(TranslateService);
  private readonly modalService = inject(ModalService);
  private readonly toastService = inject(ToastV2Service);
  private readonly toolingService = inject(ToolingService);
  public settingsService = inject(ToolingHistorySettingsService);
  public searchService = inject(ToolingHistorySearchService);
  public userRightsService = inject(UserRightsService);

  public selectedElement: string;
  public gridHeight: number = 500;
  public hasPagination: boolean = false;
  public gridScrollContainer = this.document;
  public undoLoadingState: Map<string, boolean> = new Map();
  public noDataText: GridNoDataConfigV2 = {
    withFilter: {
      text: 'There are no entries for the selected filters. Remove or change filters to find entries.',
      buttonText: 'Remove all filters',
      buttonType: 'secondary',
    },
    withSearch: {
      text: 'There are no results for your search. Change the search query to get matching results.',
    },
    withoutFilter: {
      text: 'There are currently no entries.',
      buttonText: null,
      buttonType: null,
      hidden: true,
    },
  };

  public state = this.connect(
    {
      searchData: this.searchService.state$,
      columns: this.onInit$.pipe(switchMap(() => this.createColumns())),
      responsibleCompanyFilterValue: this.searchService.responsibleCompanyFilterValue$,
    },
    this.cdr
  );

  public controlButtons: ControlButton[] = [
    {
      icon: 'file-pdf',
      label: 'COMMON_Export',
      click: (_) => {
        this.export();
      },
    },
  ];

  constructor() {
    super();
  }

  public ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.setGridHeight();

    this.searchService.state$.pipe(takeUntil(this.onDestroy$)).subscribe((state) => {
      this.hasPagination = state.total > state.pageSize;
      this.setGridHeight();
    });
  }

  @HostListener('window:resize') public setGridHeight() {
    const header = this.document.querySelector('.header-row');
    const titleHeight = 75;
    const navbarHeight = this.document.querySelector('.pages-navbar')?.clientHeight;
    const paginationSize = this.hasPagination ? 80 : 0;
    this.gridHeight =
      window.innerHeight - titleHeight - navbarHeight - header.clientHeight - 250 - paginationSize;
  }

  public isReactivateBtnDisabled(task: ToolingHistory): boolean {
    return this.toolingService.state[task.toolingId]?.isDeactivated || !task.serialTask?.isActive;
  }

  public toggleSelectedItem($event) {
    this.selectedElement = $event?.id;
  }

  private createColumns(): Observable<GridColumnV2<string>[]> {
    const columnDefs: PartialColumn[] = [
      {
        key: 'undo',
        sortable: false,
        notResizable: true,
        staticWidth: true,
        notConfigurable: true,
        title: '',
      },
      {
        key: 'toolingName',
        sortable: true,
        filter: { type: 'multiselect', items: this.getItemSource('toolingName') },
      },
      {
        key: 'manufacturer',
        sortable: true,
        filter: { type: 'multiselect', items: this.getItemSource('manufacturer') },
      },
      {
        key: 'inventoryNo',
        sortable: true,
        filter: { type: 'text' },
      },
      {
        key: 'serial',
        sortable: true,
        filter: { type: 'text' },
      },
      {
        key: 'schenckMatNo',
        sortable: true,
        filter: { type: 'text' },
      },
      {
        key: 'verificationLevel',
        sortable: true,
        filter: {
          type: 'multiselect',
          items: this.getItemSource('verificationLevel', (value) => {
            return ToolingVerificationLevelPipe.translate(value, this.translate);
          }),
        },
      },
      {
        key: 'schenckId',
        sortable: true,
        filter: { type: 'text' },
      },
      {
        key: 'customerId',
        sortable: true,
        filter: { type: 'text' },
      },
      {
        key: 'title',
        sortable: true,
        filter: { type: 'text' },
      },
      {
        key: 'priority',
        sortable: true,
        filter: {
          type: 'checkbox',
          showAllCheckbox: true,
          options: [
            { name: 'High', value: 'High' },
            { name: 'Medium', value: 'Medium' },
            { name: 'Low', value: 'Low' },
          ],
        },
      },
      {
        key: 'responsibleCompany',
        sortable: true,
        filter: {
          type: 'radio',
          options: [
            { value: 'Customer', name: 'My' },
            { value: 'Schenck', name: 'Schenck' },
          ],
          displayAllOption: true,
        },
      },
      {
        key: 'responsible',
        sortable: true,
        filter: { type: 'text' },
      },
      {
        key: 'executive',
        sortable: true,
        filter: {
          type: 'text',
        },
      },
      {
        key: 'dueDate',
        sortable: true,
        filter: { type: 'date' },
      },
      {
        key: 'executionDate',
        sortable: true,
        filter: { type: 'date' },
      },
      {
        key: 'description',
        sortable: true,
        filter: {
          type: 'text',
        },
      },
      {
        key: 'type',
        sortable: true,
        filter: { type: 'multiselect', items: this.getItemSource('type'), withCustomValue: false },
      },
      { key: 'expectedDurationMinutes', sortable: true },
      {
        key: 'scheduling',
        sortable: true,
        filter: createSchedulingFilter(this.searchService.allResults$, this.translate),
      },
      {
        key: 'machines',
        sortable: true,
        filter: {
          type: 'text',
        },
      },
      {
        key: 'toolingForRotor',
        sortable: true,
        filter: {
          type: 'text',
        },
      },
      {
        key: 'mainContact',
        sortable: true,
        filter: {
          type: 'text',
        },
      },
      {
        key: 'storageLocation',
        sortable: true,
        filter: {
          type: 'text',
        },
      },
      {
        key: 'workplace',
        sortable: true,
        filter: {
          type: 'text',
        },
      },
    ];

    return this.settingsService.createColumns$(
      addTitleAndWidthToGridColumn(columnDefs, toolingHistoryColumnsBaseConfig)
    );
  }

  private getItemSource(entity: keyof ToolingHistory, translateFn?: (value: any) => string) {
    return this.searchService.getMultiselectItemSource(entity, translateFn);
  }

  public refineSearch(state: GridStateChangeV2): void {
    this.searchService.search({
      filter: <any>state.filter,
      fulltext: state.fulltext,
      page: state.page,
      sort: <any>state.sort,
    });
  }

  public keyupEnterSearch(searchText: string): void {
    this.searchService.search({ fulltext: searchText, page: 0 });
  }

  public reactToPageSizeChange(pageSize: number): void {
    this.searchService.search({ pageSize });
  }

  public updateColumnSettings(columns: GridColumnV2[]): void {
    this.settingsService.updateColumnSettings(columns);
  }

  public openTask(task: ToolingHistory): void {
    this.selectedElement = this.selectedElement === task.id ? '' : task.id;
    this.modalService.open<string>(ToolingHistoryModalComponent, task.id);
  }

  public undoTask(task: ToolingHistory, event: Event): void {
    this.undoLoadingState.set(task.id, true);
    event.stopPropagation();

    this.taskService
      .undo$(task.id)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((response: BackendResponse.Response<void>) => {
        if ((response as BackendResponse.ErrorResponse).error) {
          this.handleUndoErrorResponse(response as BackendResponse.ErrorResponse);
        }
        this.undoLoadingState.delete(task.id);
        this.cdr.detectChanges();
      });
  }

  public export(): void {
    this.downloadService.downloadExecutedToolingTaskPDF(this.state.searchData.selectedIds);
  }

  public isSelected(id: string): boolean {
    return this.state.searchData.selectedIds.includes(id) || this.selectedElement === id;
  }

  private handleUndoErrorResponse(error: BackendResponse.ErrorResponse): void {
    if ((error.type = 'NotFound')) {
      this.toastService.open({
        type: 'error',
        text: `The task can't be undone, because the series it belongs to was already deleted.`,
      });
      return;
    }

    openErrorToast(this.toastService)(error);
  }
}
